import React from 'react'
import { InfoCircleOutlined } from '@ant-design/icons'
import { Checkbox, Form, Popover, Select, Table, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/lib/table'
import { useSelector } from 'react-redux'
import { getMunicipality } from '../../../store/beslutsstod/municipalitySlice'
import { MunicipalityData, TimepointTask } from '../types/municipalityData'
import { JSONLoadError } from '../utils/jsonLoader'

const { Option } = Select
const { Text } = Typography

type Props = {
  timepoint: string
  tasks: TimepointTask[]
  loadingErrors?: JSONLoadError[]
  allTimepoints: string[]
}

const getAllocationCount = (taskName: string, formValues: any, timepoints: string[]): number => {
  let count = 0
  for (const tp of timepoints) {
    const taskData = formValues?.[tp]?.timepoints?.[taskName]
    if (taskData && (taskData.Besök != null || taskData.Nivå != null)) {
      count++
    }
  }
  return count
}

const isTaskAllocatedInTimepoint = (taskName: string, timepointName: string, formValues: any): boolean => {
  const taskData = formValues?.[timepointName]?.timepoints?.[taskName]
  return taskData && (taskData.Besök != null || taskData.Nivå != null)
}

// "-" bär insatsbeskrivningen och visas utan prefix överst. Resten är nivåer, i
// samma ordning som nivåväljaren.
const selectionsDescription = (task: TimepointTask) => {
  const taskDescription = task.selectionDescriptions['-']
  const levels = task.selections.filter((level) => level && level !== '-' && task.selectionDescriptions[level])

  return (
    <>
      {taskDescription && <div style={{ whiteSpace: 'pre-line', marginBottom: levels.length ? 12 : 0 }}>{taskDescription}</div>}
      {levels.map((level) => (
        <div key={level} style={{ whiteSpace: 'pre-line', marginBottom: 2 }}>
          <Text strong>{level}: </Text>
          {task.selectionDescriptions[level]}
        </div>
      ))}
    </>
  )
}

const TimepointTaskTable: React.FC<Props> = ({ timepoint, tasks, loadingErrors, allTimepoints }) => {
  const municipalityData = useSelector(getMunicipality) as MunicipalityData
  const form = Form.useFormInstance()
  const formValues = Form.useWatch([], form)

  const showDoubleColumn = tasks.some((t) => t.double)
  const showDescription = municipalityData?.input.formSettings.showDescription

  // En insats som redan är vald här får alltid ändras eller rensas, annars går en
  // bedömning som bryter mot reglerna inte att rätta.
  const isSelectedHere = (taskName: string) => isTaskAllocatedInTimepoint(taskName, timepoint, formValues)

  const exclusiveTaskInTimepoint = tasks.find((t) => t.exclusive && isSelectedHere(t.name))

  const getWarning = (task: TimepointTask): string | null => {
    if (isSelectedHere(task.name)) return null

    if (task.maxAllocations != null && getAllocationCount(task.name, formValues, allTimepoints) >= task.maxAllocations) {
      return `Kan bara väljas på ${task.maxAllocations === 1 ? 'en tidpunkt' : `${task.maxAllocations} tidpunkter`}`
    }
    if (task.exclusive && tasks.some((t) => t.name !== task.name && isSelectedHere(t.name))) {
      return 'Kan inte kombineras med andra insatser'
    }
    return null
  }

  // Rader som blockeras av att en insats som ska vara ensam redan ligger här gråas
  // utan förklaring - en ikon per rad gör tabellen oläslig när alla utom en låses.
  const isBlockedByExclusive = (task: TimepointTask): boolean => !isSelectedHere(task.name) && !!exclusiveTaskInTimepoint

  const isRowDisabled = (task: TimepointTask): boolean => getWarning(task) !== null || isBlockedByExclusive(task)

  const hasLoadingError = (taskName: string, errorType: 'frequency' | 'selection') =>
    !!loadingErrors?.some((e) => e.taskName === taskName && e.taskType === 'timepoint' && e.timepoint === timepoint && e.errorType === errorType)

  const columns: ColumnsType<TimepointTask> = [
    {
      title: 'Insats',
      dataIndex: 'name',
      key: 'name',
      width: '30%',
      render: (_, task) => {
        const warning = getWarning(task)
        return (
          <>
            <Text type={isRowDisabled(task) ? 'secondary' : undefined}>{task.name}</Text>
            {warning && (
              <Tooltip title={warning}>
                <InfoCircleOutlined style={{ marginLeft: 4, color: '#999' }} />
              </Tooltip>
            )}
          </>
        )
      },
    },
    {
      title: 'Besök per vecka',
      key: 'besok',
      onCell: (task) => ({ className: hasLoadingError(task.name, 'frequency') ? 'beslutsstod-cell-error' : undefined }),
      width: showDoubleColumn ? '25%' : '35%',
      render: (_, task) => {
        const disabled = isRowDisabled(task)
        return (
          <Form.Item name={[timepoint, 'timepoints', task.name, 'Besök']} noStyle>
            <Select
              bordered={false}
              placeholder="-"
              allowClear
              disabled={disabled}
              listHeight={400}
              style={{ width: '100%' }}
            >
              {task.frequencies?.map((freq) => (
                <Option key={freq.name} value={freq.timesPerWeek}>
                  {freq.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )
      },
    },
    {
      title: 'Nivå',
      key: 'niva',
      onCell: (task) => ({ className: hasLoadingError(task.name, 'selection') ? 'beslutsstod-cell-error' : undefined }),
      width: showDoubleColumn ? '25%' : '35%',
      render: (_, task) => {
        const disabled = isRowDisabled(task)
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Form.Item name={[timepoint, 'timepoints', task.name, 'Nivå']} noStyle>
              <Select
                bordered={false}
                placeholder="-"
                allowClear
                disabled={disabled}
                listHeight={400}
                style={{ width: '100%' }}
              >
                {/* "-" och namnlösa nivåer är inte val, de bär insatsbeskrivningen. Rensa med krysset. */}
                {task.selections
                  .filter((sel) => sel && sel !== '-')
                  .map((sel) => (
                    <Option value={sel} key={sel}>
                      {sel}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
            {showDescription && (
              <Popover overlayClassName="decision-popover" overlayStyle={{ width: 500 }} placement="topRight" title={task.description} content={selectionsDescription(task)} className="ml-1">
                <InfoCircleOutlined />
              </Popover>
            )}
          </div>
        )
      },
    },
  ]

  if (showDoubleColumn) {
    columns.push({
      title: 'Dubbelbemanning',
      key: 'dubbel',
      width: '20%',
      render: (_, task) => {
        if (!task.double) return null
        const disabled = isRowDisabled(task)
        return (
          <Form.Item name={[timepoint, 'timepoints', task.name, 'Dubbelbemanning']} valuePropName="checked" noStyle>
            <Checkbox disabled={disabled} />
          </Form.Item>
        )
      },
    })
  }

  return (
    <div className="mb-10">
      <Typography.Title level={3} className="mb-2 text-left">
        {timepoint}
      </Typography.Title>
      <Table<TimepointTask> className="beslutsstod-task-table" size="small" bordered columns={columns} dataSource={tasks} rowKey="name" pagination={false} />
    </div>
  )
}

export default TimepointTaskTable
